import { useEffect, useMemo, useState } from 'react'
import { Menu, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { OrganizationSidebarContent } from '@/components/OrganizationSidebar'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useNavigate } from 'react-router'
import { couplesApi, type CoupleSummary } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { OrgPermission } from '@/utils/permissions'

type StatusType = 'Completed' | 'Ongoing' | 'Approval' | 'Pending'

const filters = [
  'All',
  'Pending Accepteance',
  'Ongoing Assessment',
  'Completed Assessment',
  'Certification Approval',
]

const statusFilters: Record<string, string[]> = {
  'Pending Accepteance': ['invited', 'registered'],
  'Ongoing Assessment': ['in_progress'],
  'Completed Assessment': ['completed'],
  'Certification Approval': ['certified'],
}

const normalizeStatus = (status?: string | null) =>
  (status ?? '').toLowerCase().replace(/[-\s]/g, '_')

const toUiStatus = (status?: string | null): StatusType => {
  const normalized = normalizeStatus(status)
  if (normalized === 'completed') return 'Completed'
  if (normalized === 'in_progress') return 'Ongoing'
  if (normalized === 'certified') return 'Approval'
  return 'Pending'
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(/ /g, '-')
}

const formatPersonName = (
  person?: { firstName?: string | null; lastName?: string | null } | null
) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()

const buildPagination = (current: number, total: number) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, idx) => idx + 1)
  }
  const pages: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  if (start > 2) pages.push('ellipsis')
  for (let page = start; page <= end; page += 1) pages.push(page)
  if (end < total - 1) pages.push('ellipsis')
  pages.push(total)

  return pages
}

export default function CoupleManagement() {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [couples, setCouples] = useState<CoupleSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFilter, setSelectedFilter] = useState<string>('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const getStatusBadge = (status: StatusType) => {
    const styles: Record<StatusType, string> = {
      Completed: 'bg-[#d1f4e0] text-[#15803d]',
      Ongoing: 'bg-[#fef3c7] text-[#d97706]',
      Approval: 'bg-[#fee2e2] text-[#dc2626]',
      Pending: 'bg-[#fef3c7] text-[#d97706]',
    }

    return (
      <span
        className={`px-3 py-1 rounded-full text-[12px] font-medium ${styles[status]}`}
      >
        {status}
      </span>
    )
  }

  useEffect(() => {
    let active = true

    const fetchCouples = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await couplesApi.list()
        const payload =
          (response as { data?: CoupleSummary[] }).data ||
          (response as unknown as CoupleSummary[])
        if (!active) return
        setCouples(Array.isArray(payload) ? payload : [])
      } catch (err) {
        if (!active) return
        console.error('Failed to load couples:', err)
        setError((err as Error)?.message || 'Failed to load couples')
      } finally {
        if (active) setLoading(false)
      }
    }

    void fetchCouples()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedFilter, searchQuery, rowsPerPage])

  const filteredCouples = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return couples.filter((couple) => {
      const normalizedStatus = normalizeStatus(couple.status)
      if (selectedFilter !== 'All') {
        const allowed = statusFilters[selectedFilter] || []
        if (!allowed.includes(normalizedStatus)) return false
      }

      if (!query) return true
      const personOneName = formatPersonName(couple.personOne)
      const personTwoName = formatPersonName(couple.personTwo)
      const contract = couple.contractNumber || couple.contract || ''
      const haystack = [couple.id, contract, personOneName, personTwoName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [couples, searchQuery, selectedFilter])

  const totalPages = Math.max(1, Math.ceil(filteredCouples.length / rowsPerPage))
  const safePage = Math.min(currentPage, totalPages)

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage)
    }
  }, [currentPage, safePage])

  const pagedCouples = useMemo(() => {
    const start = (safePage - 1) * rowsPerPage
    return filteredCouples.slice(start, start + rowsPerPage)
  }, [filteredCouples, rowsPerPage, safePage])

  const paginationItems = useMemo(
    () => buildPagination(safePage, totalPages),
    [safePage, totalPages]
  )

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      {/* Main Content */}
      <div className='p-4 md:p-6 lg:p-8'>
        {/* Full White Container */}
        <div className='bg-white rounded-[24px] overflow-hidden'>
          {/* Header Section */}
          <div className='px-4 pt-4 pb-4 sm:px-6 sm:pt-6 sm:pb-5 lg:px-8 lg:pt-8 lg:pb-6'>
            {/* Top Row - User Profile */}
            <div className='flex items-center justify-between mb-6'>
              {/* Mobile Menu Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <button className='lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors'>
                    <Menu className='h-6 w-6 text-gray-700' />
                  </button>
                </SheetTrigger>
                <SheetContent side='left' className='p-0 w-[280px]'>
                  <OrganizationSidebarContent />
                </SheetContent>
              </Sheet>

              {/* User Profile (Desktop) */}
              <div className='hidden lg:flex items-center gap-4 ml-auto'>
                <div className='flex items-center gap-3'>
                  <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                    {initials}
                  </div>
                  <span className='text-[20px] font-bold text-[#353535] leading-[32px]'>{displayName}</span>
                </div>
                <button className='w-6 h-6 flex items-center justify-center'>
                  <svg width='4' height='16' viewBox='0 0 4 16' fill='none'>
                    <circle cx='2' cy='2' r='2' fill='#6c6c6c' />
                    <circle cx='2' cy='8' r='2' fill='#6c6c6c' />
                    <circle cx='2' cy='14' r='2' fill='#6c6c6c' />
                  </svg>
                </button>
              </div>
            </div>

            {/* Title and Description */}
            <div className='mb-4 sm:mb-6 text-start'>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515] leading-[36px] sm:leading-[48px] mb-1'>
                Couple Management
              </h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d] font-normal leading-[20px] sm:leading-[24px]'>
                Lorem ipsum dolor sit amet consectetur. Id tempor malesuada dignissim nam feugiat donec mauris proin cum. Aliquet cras consectetur risus mi tortor adipiscing. Felis sodales lectus a lectus risus. Sed elementum congue.
              </p>
            </div>

            {/* Search, Filters, and Invite Button Row */}
            <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
              {/* Search */}
              <div className='relative w-full sm:w-[200px]'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6c6c6c]' />
                <input
                  type='text'
                  placeholder='Search'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='w-full pl-10 pr-4 py-2 bg-white border border-[#e0e0e0] rounded-[8px] text-[14px] text-[#4e4d4d] placeholder-[#6c6c6c] focus:outline-none focus:ring-1 focus:ring-[#FF6600]'
                />
              </div>

              {/* Filter Tabs */}
              <div className='flex gap-2 overflow-x-auto pb-1 -mx-1 px-1'>
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setSelectedFilter(filter)}
                    className={`px-3 sm:px-4 py-2 rounded-[8px] text-[13px] sm:text-[14px] font-bold transition-colors whitespace-nowrap shrink-0 ${
                      selectedFilter === filter
                        ? 'bg-[#151515] text-white'
                        : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>

              {/* Invite Couple Button */}
              {hasPermission(OrgPermission.INVITE_COUPLES) && (
                <Button
                  onClick={() => navigate('/organization/invitation')}
                  className='w-full sm:w-auto sm:ml-auto bg-[#151515] hover:bg-[#2a2a2a] text-white px-6 py-2 rounded-[8px] text-[14px] font-bold h-[40px]'
                >
                  Invite Couple
                </Button>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className='px-4 sm:px-6 lg:px-8 overflow-x-auto'>
            <Table className='min-w-[900px]'>
              <TableHeader>
                <TableRow className='border-b border-[#eaecf0]'>
                  <TableHead className='w-12 py-4'>
                    <input type='checkbox' className='rounded border-gray-300' />
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      S/N
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      Contract
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      Couple Names
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      Request Date
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      Last Edition
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>
                    <div className='flex items-center gap-1'>
                      Status
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
                      </svg>
                    </div>
                  </TableHead>
                  <TableHead className='font-medium text-[14px] text-[#475467] py-4'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className='text-center text-[14px] text-[#4e4d4d] py-8'>
                      Loading couples...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={8} className='text-center text-[14px] text-red-600 py-8'>
                      {error}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && pagedCouples.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className='text-center text-[14px] text-[#4e4d4d] py-8'>
                      No couples found.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && pagedCouples.map((row, idx) => {
                  const contractLabel = row.contractNumber || row.contract || '-'
                  const requestDate = formatDate(row.requestDate || row.createdAt)
                  const lastEdition = formatDate(row.lastEditDate || row.updatedAt)
                  const displayStatus = toUiStatus(row.status)
                  const rowNumber = (safePage - 1) * rowsPerPage + idx + 1
                  return (
                    <TableRow key={row.id} className='border-b border-[#eaecf0] hover:bg-[#f9fafb]'>
                      <TableCell className='py-4 text-start'>
                        <input type='checkbox' className='rounded border-gray-300' />
                      </TableCell>
                      <TableCell className='font-normal text-[14px] text-[#101828] py-4 text-start'>{rowNumber}</TableCell>
                      <TableCell className='font-normal text-[14px] text-[#101828] py-4 text-start'>{contractLabel}</TableCell>
                      <TableCell className='text-[14px] text-[#101828] py-4 text-start'>
                        <div>{formatPersonName(row.personOne) || '-'}</div>
                        <div className='text-[#475467]'>{formatPersonName(row.personTwo) || '-'}</div>
                      </TableCell>
                      <TableCell className='text-[14px] text-[#475467] py-4 text-start'>{requestDate}</TableCell>
                      <TableCell className='text-[14px] text-[#475467] py-4 text-start'>{lastEdition}</TableCell>
                      <TableCell className='py-4 text-start'>{getStatusBadge(displayStatus)}</TableCell>
                      <TableCell className='py-4 text-start'>
                        <Button
                          onClick={() => navigate(`/organization/couples/${row.id}`)}
                          className='bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-[8px] text-[12px] font-bold h-[36px]'
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className='flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 py-4'>
            <div className='flex items-center gap-2'>
              <span className='text-[14px] text-[#475467]'>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className='border border-[#d0d5dd] rounded-[8px] px-3 py-1.5 text-[14px] text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#FF6600] bg-white'
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className='flex items-center gap-1'>
              <button
                className='w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#e0e0e0] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={safePage <= 1}
                aria-label='Previous page'
              >
                <ChevronLeft className='h-4 w-4 text-[#475467]' />
              </button>
              {paginationItems.map((item, idx) => {
                if (item === 'ellipsis') {
                  return (
                    <span key={`ellipsis-${idx}`} className='px-2 text-[14px] text-[#475467]'>
                      ...
                    </span>
                  )
                }
                const isActive = item === safePage
                return (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`w-[32px] h-[32px] text-[14px] font-medium rounded-full ${
                      isActive
                        ? 'bg-[#FF6600] text-white'
                        : 'text-[#475467] hover:bg-[#f9fafb]'
                    }`}
                  >
                    {item}
                  </button>
                )
              })}
              <button
                className='w-[32px] h-[32px] flex items-center justify-center rounded-full border border-[#e0e0e0] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={safePage >= totalPages}
                aria-label='Next page'
              >
                <ChevronRight className='h-4 w-4 text-[#475467]' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
